import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ChevronDown, X } from "lucide-react";
import { BookingButton } from "@/components/BookingButton";
import { submitEventInquiry } from "@/lib/event-inquiries.functions";
import { getOnsitePageData } from "@/lib/onsite.functions";
import type { OnsiteAmenity } from "@/lib/onsite.functions";
import { useGalleryGestureLock } from "@/hooks/use-gallery-gesture-lock";
import {
  getGroupsEventsPageData,
  type GroupsEventsContent,
  type GroupsEventsCard as GeCard,
  type GroupsEventsFaq,
  type GroupsEventsGalleryImage,
} from "@/lib/groups-events.functions";
import type { CategoryComponentProps } from "./index";

import pathHero from "@/assets/path-hero.jpg.asset.json";
import tentNight from "@/assets/tent-night-sky.png.asset.json";
import lounge from "@/assets/lounge-interior.jpg.asset.json";
import hotTub from "@/assets/hot-tub-exterior.jpg.asset.json";
import suiteExterior from "@/assets/nature-suite/exterior-wide.jpg.asset.json";
import suiteFamily from "@/assets/nature-suite/family-view.jpg.asset.json";
import patio from "@/assets/nature-suite/patio-couple.jpg.asset.json";
import bedMorning from "@/assets/nature-suite/bed-morning.jpg.asset.json";

type EventCard = {
  id: string;
  anchor: string;
  title: string;
  description: string;
  bestFor: string;
  inclusions: string;
  cta: string;
  image: string;
};

const EVENT_CARDS: EventCard[] = [
  {
    id: "weddings",
    anchor: "weddings",
    title: "Weddings & Elopements",
    description:
      "For couples who want something intimate, beautiful, and different from a traditional ballroom wedding.",
    bestFor: "Elopements, micro-weddings, vow renewals, and wedding weekends.",
    inclusions:
      "Ceremony space, lodging, rehearsal dinner, private dinner, beverage service, unspoiled dark skies, campfire, breakfast, and help arranging local activities for guests.",
    cta: "View Wedding Sample",
    image: patio.url,
  },
  {
    id: "corporate-retreats",
    anchor: "corporate-retreats",
    title: "Corporate Retreats",
    description:
      "For leadership teams, executive offsites, creative planning sessions, and team connection.",
    bestFor:
      "Leadership retreats, planning offsites, team retreats, incentive trips, and small company gatherings.",
    inclusions:
      "Lodging, meeting space, meals, beverage service, campfire discussions, unspoiled dark skies, onsite activities, and help coordinating offsite adventures.",
    cta: "View Retreat Sample",
    image: lounge.url,
  },
  {
    id: "family-reunions",
    anchor: "family-reunions",
    title: "Family Reunions",
    description:
      "For families who want everyone together without the chaos of a hotel block or vacation rental scramble.",
    bestFor:
      "Milestone birthdays, multigenerational trips, holiday gatherings, and family celebrations.",
    inclusions:
      "Lodging, casual group meals, kayaking, beach time, lawn games, campfire, unspoiled dark skies, and help coordinating Grand Canyon, Sedona, or local day trips.",
    cta: "View Reunion Sample",
    image: suiteFamily.url,
  },
  {
    id: "wellness-retreats",
    anchor: "wellness-retreats",
    title: "Wellness, Creative & Educational Retreats",
    description:
      "For retreat leaders, coaches, educators, creators, and small groups looking for a peaceful place to gather.",
    bestFor:
      "Yoga retreats, photography retreats, faith-based retreats, writing groups, workshops, and hosted experiences.",
    inclusions:
      "Lodging, meal packages, flexible outdoor gathering spaces, quiet time, trails, campfire, unspoiled dark skies, and help arranging guided hikes, tours, or other regional experiences.",
    cta: "View Retreat Sample",
    image: tentNight.url,
  },
  {
    id: "private-celebrations",
    anchor: "private-celebrations",
    title: "Private Celebrations & Special Events",
    description:
      "For birthdays, anniversaries, proposal weekends, small holiday events, and one-of-a-kind gatherings.",
    bestFor:
      "Groups who want an incredible private setting to celebrate, it can't get much better than this.",
    inclusions:
      "Dinner, lodging, beach time, campfire, private activities, unspoiled dark skies, beverage service, and custom touches.",
    cta: "View Celebration Sample",
    image: hotTub.url,
  },
];

type SampleItinerary = {
  anchor: string;
  title: string;
  bestFor: string;
  length: string;
  price: string;
  inclusions: string[];
  sponsor: string[];
  days: { title: string; items: string[] }[];
};

const SAMPLES: SampleItinerary[] = [
  {
    anchor: "weddings",
    title: "Micro-Wedding Weekend",
    bestFor: "20 overnight guests",
    length: "2 nights",
    price:
      "From $24,000–$42,000 before outside rentals, specialty vendors, and optional offsite activities",
    inclusions: [
      "Two-night lodging block or full-property buyout",
      "Ceremony location",
      "Welcome campfire with s’mores",
      "Rehearsal or welcome dinner",
      "Wedding dinner",
      "Beverage service",
      "Continental breakfast each morning",
      "Stargazing experience",
      "Access to onsite activities",
      "Help arranging optional guest activities",
      "Planning coordination call related to Backland services",
    ],
    sponsor: [
      "Wedding planner or day-of coordinator",
      "Floral, décor, photography, music, rentals, tenting, setup, teardown, and other wedding vendors",
      "Mobile restrooms if more than 30 people will be onsite",
    ],
    days: [
      {
        title: "Day 1 — Arrival",
        items: [
          "4:00 pm — Guest arrival and suite check-in",
          "5:30 pm — Welcome drinks or casual gathering",
          "6:30 pm — Rehearsal or welcome dinner",
          "8:00 pm — Campfire and s’mores",
          "9:00 pm — Stargazing under the dark sky",
        ],
      },
      {
        title: "Day 2 — Wedding Day",
        items: [
          "8:00 am — Continental breakfast",
          "Morning — Relax, get ready, explore the property",
          "Optional morning activity — Kayaking, beach time, or short nature walk",
          "4:30 pm — Ceremony",
          "5:00 pm — Photos and cocktail hour",
          "6:30 pm — Wedding dinner",
          "8:30 pm — Campfire, dessert, unspoiled dark skies, and private celebration",
        ],
      },
      {
        title: "Day 3 — Farewell",
        items: [
          "8:00 am — Breakfast",
          "10:00 am — Farewell walk or optional family photos",
          "11:00 am — Checkout",
        ],
      },
    ],
  },
  {
    anchor: "corporate-retreats",
    title: "Executive Corporate Retreat",
    bestFor: "10–20 attendees",
    length: "2 nights",
    price:
      "From $18,000–$34,000 before optional offsite activities, transportation, specialty facilitators, or rentals",
    inclusions: [
      "Two-night lodging",
      "Breakfast each morning",
      "Two group dinners",
      "One or two lunches or box lunches",
      "Meeting space or facilitated gathering setup",
      "Coffee, drinks, and snacks",
      "Beverage service",
      "Campfire gathering",
      "Stargazing experience",
      "Optional onsite team-building activity",
      "Help coordinating offsite group adventures",
    ],
    sponsor: [
      "Retreat facilitator or event coordinator",
      "AV equipment, specialty meeting equipment, printed materials, or rentals",
      "Transportation for offsite activities",
      "Any outside speakers, coaches, trainers, or facilitators",
    ],
    days: [
      {
        title: "Day 1 — Arrive & Reset",
        items: [
          "3:00–4:00 pm — Arrival and check-in",
          "5:00 pm — Welcome reception",
          "6:30 pm — Group dinner",
          "8:00 pm — Fireside conversation: goals for the retreat",
          "9:00 pm — Stargazing",
        ],
      },
      {
        title: "Day 2 — Strategy & Adventure",
        items: [
          "8:00 am — Breakfast",
          "9:00 am — Strategy session",
          "12:00 pm — Lunch",
          "1:30 pm — Breakouts or leadership discussion",
          "3:30 pm — Onsite activity: kayaking, trails, disc golf, or guided nature walk",
          "6:30 pm — Private dinner",
          "8:00 pm — Campfire debrief",
          "Optional alternative — Guided Grand Canyon hiking tour, Sedona hiking tour, jeep tour, or helicopter tour, depending on group goals and vendor availability",
        ],
      },
      {
        title: "Day 3 — Commitments & Departure",
        items: [
          "8:00 am — Breakfast",
          "9:00 am — Final planning session",
          "10:30 am — Commitments and next steps",
          "11:00 am — Checkout",
        ],
      },
    ],
  },
  {
    anchor: "family-reunions",
    title: "Family Reunion Weekend",
    bestFor:
      "20–30 family members, depending on sleeping arrangements and onsite guest count",
    length: "2 nights",
    price:
      "From $16,000–$32,000 before optional offsite activities, transportation, specialty rentals, or added event infrastructure",
    inclusions: [
      "Lodging for the group",
      "Continental breakfast",
      "One casual group dinner",
      "One upgraded dinner or private meal",
      "Beverage service",
      "Campfire and s’mores",
      "Kayaking, beach time, lawn games, and trails",
      "Stargazing",
      "Optional box lunches",
      "Help arranging Grand Canyon, Sedona, Williams, or local activities",
    ],
    sponsor: [
      "Family event coordinator or point person",
      "Decorations, reunion shirts, games, printed schedules, family photo coordination, or specialty rentals",
      "Transportation for offsite outings",
    ],
    days: [
      {
        title: "Day 1 — Everyone Arrives",
        items: [
          "4:00 pm — Check-in",
          "5:30 pm — Family welcome gathering",
          "6:30 pm — Casual dinner",
          "8:00 pm — Campfire, s’mores, and lawn games",
        ],
      },
      {
        title: "Day 2 — Explore & Reconnect",
        items: [
          "8:00 am — Breakfast",
          "10:00 am — Kayaking, beach time, trails, and lawn games",
          "12:00 pm — Box lunches or casual lunch",
          "Afternoon option 1 — Relax onsite at the beach and pond",
          "Afternoon option 2 — Grand Canyon day trip or guided hiking tour",
          "Afternoon option 3 — Williams outing or scenic local activity",
          "6:30 pm — Celebration dinner",
          "8:30 pm — Stargazing and family photos under the stars",
        ],
      },
      {
        title: "Day 3 — Farewell",
        items: [
          "8:00 am — Breakfast",
          "9:30 am — Final family walk or group photo",
          "11:00 am — Checkout",
        ],
      },
    ],
  },
  {
    anchor: "wellness-retreats",
    title: "Wellness, Creative & Educational Retreat",
    bestFor: "10–24 guests",
    length: "2 or 3 nights",
    price:
      "From $17,000–$36,000 before outside instructors, facilitators, transportation, rentals, or offsite activities",
    inclusions: [
      "Lodging",
      "Breakfast",
      "Lunches or box lunches",
      "Group dinners",
      "Beverage service",
      "Flexible gathering space",
      "Sunrise or sunset outdoor sessions",
      "Stargazing",
      "Campfire",
      "Optional private meal or welcome gift",
      "Help arranging guided hikes, Sedona outings, photography tours, or wellness providers",
    ],
    sponsor: [
      "Retreat leader or facilitator",
      "Event coordinator or assistant",
      "Mats, props, sound equipment, teaching materials, printed materials, or specialty rentals",
      "Outside instructors, guides, coaches, or practitioners",
      "Setup and teardown for retreat-specific equipment",
    ],
    days: [
      {
        title: "Day 1 — Arrival & Opening",
        items: [
          "4:00 pm — Arrival and check-in",
          "5:30 pm — Opening circle or welcome session",
          "6:30 pm — Dinner",
          "8:00 pm — Campfire and intention setting",
        ],
      },
      {
        title: "Day 2 — Deep Retreat Day",
        items: [
          "7:30 am — Sunrise yoga, meditation, photography walk, or workshop",
          "8:30 am — Breakfast",
          "10:00 am — Main retreat session",
          "12:30 pm — Lunch",
          "2:00 pm — Quiet time, nature immersion, or coaching sessions",
          "Optional afternoon activity — Guided nature walk, Sedona hiking tour, Grand Canyon hiking tour, or onsite beach time",
          "5:00 pm — Group session",
          "6:30 pm — Dinner",
          "9:00 pm — Stargazing",
        ],
      },
      {
        title: "Day 3 — Integration",
        items: [
          "8:00 am — Breakfast",
          "9:00 am — Closing session",
          "11:00 am — Checkout",
        ],
      },
    ],
  },
  {
    anchor: "private-celebrations",
    title: "Private Celebration",
    bestFor:
      "Birthdays, anniversaries, proposal weekends, milestone gatherings, and small private events",
    length: "1–2 nights",
    price:
      "From $7,500–$20,000 before optional rentals, transportation, offsite tours, or custom vendors",
    inclusions: [
      "Lodging block or partial property reservation",
      "Celebration dinner",
      "Beverage service",
      "Campfire and s’mores",
      "Stargazing",
      "Optional romance package, birthday cake, welcome gifts, or custom touches",
      "Breakfast",
      "Help arranging optional activities or tours",
    ],
    sponsor: [
      "Event point person or coordinator",
      "Decorations, specialty rentals, entertainment, photographer, or outside vendors",
      "Transportation for offsite experiences",
      "Setup and teardown for custom décor or rentals",
    ],
    days: [
      {
        title: "Day 1",
        items: [
          "4:00 pm — Arrival",
          "5:30 pm — Welcome gathering",
          "6:30 pm — Celebration dinner",
          "8:00 pm — Campfire, dessert, and unspoiled dark skies",
        ],
      },
      {
        title: "Day 2",
        items: [
          "8:00 am — Breakfast",
          "Morning option 1 — Kayaking, beach, trails, or quiet time onsite",
          "Morning option 2 — Grand Canyon helicopter tour, jeep tour, guided hike, or Sedona day trip",
          "Optional second night or departure",
        ],
      },
    ],
  },
];

const ONSITE_EXPERIENCES = [
  "Stargazing with telescopes",
  "Nightly campfire with s’mores",
  "Kayaking the pond",
  "Sand beach and lawn games",
  "Walking trails",
  "Bird watching",
  "Disc golf",
  "Volleyball",
  "Quiet time in nature",
];

const OFFSITE_EXPERIENCES: { title: string; body: string; image: string }[] = [
  {
    title: "Grand Canyon Hiking Tours",
    body: "Guided hiking experiences at or near the Grand Canyon, customized to your group’s ability level and schedule.",
    image: pathHero.url,
  },
  {
    title: "Sedona Hiking Tours",
    body: "Guided red rock hiking experiences in Sedona for groups who want a scenic day trip.",
    image: suiteExterior.url,
  },
  {
    title: "Grand Canyon Helicopter Tours",
    body: "A dramatic way to experience the Grand Canyon from above.",
    image: tentNight.url,
  },
  {
    title: "Jeep Tours",
    body: "Guided off-road or scenic jeep tours in nearby destinations such as Sedona or the Grand Canyon area.",
    image: hotTub.url,
  },
  {
    title: "Scenic Day Trips",
    body: "Custom group outings to Williams, Flagstaff, Sedona, the Grand Canyon, or other Northern Arizona highlights.",
    image: suiteFamily.url,
  },
  {
    title: "Photography Experiences",
    body: "Sunrise, sunset, night sky, landscape, or wildlife photography outings.",
    image: bedMorning.url,
  },
  {
    title: "Wellness & Guided Outdoor Sessions",
    body: "Yoga, meditation, breathwork, guided walks, or other instructor-led experiences, depending on vendor availability.",
    image: patio.url,
  },
  {
    title: "Custom Group Adventures",
    body: "If your group has something specific in mind, we can help point you in the right direction and coordinate with local providers where possible.",
    image: lounge.url,
  },
];

const FAQS: { q: string; a: string }[] = [
  {
    q: "How many people can Backland accommodate overnight?",
    a: "Backland has 10 luxury suites. Each suite includes a king bed and queen sofa sleeper. Three of the suites also have a bunk bed. Additional sleeping arrangements may be possible depending on the event. For the best experience, Backland is ideal for intimate groups rather than very large events.",
  },
  {
    q: "Can we book the whole property?",
    a: "Yes — full-property buyouts may be available depending on dates, group size, and event type. This is often the cleanest option for weddings, corporate retreats, family reunions, and private retreats.",
  },
  {
    q: "What meals can be included?",
    a: "Backland can support group dining through its onsite restaurant, continental breakfast, dinner, box lunches, snacks, drinks, beverage service, and custom meal planning depending on the event. Groups may also request to use approved outside catering.",
  },
  {
    q: "How far in advance should we inquire?",
    a: "For the best availability, we recommend inquiring at least six months in advance, especially if you are hoping to reserve the full property or bring a group during peak season.",
  },
  {
    q: "Can guests park near their suites?",
    a: "Yes. Backland offers convenient parking access for overnight guests, and EV charging is available near reception.",
  },
  {
    q: "Is Wi-Fi available?",
    a: "Yes — both in the lobby and in each tent.",
  },
  {
    q: "Can we bring outside vendors?",
    a: "Outside vendors are allowed with prior approval. We can discuss catering, photography, florals, music, rentals, facilitators, transportation, and other event needs during the planning process.",
  },
  {
    q: "Is amplified music allowed?",
    a: "Because Backland is a peaceful nature resort, amplified music and late-night noise may be limited. Tell us what you have in mind and we’ll help determine what works best.",
  },
  {
    q: "Will we need mobile restrooms?",
    a: "For events with more than 30 people onsite, mobile restrooms may be required and are typically coordinated by the event sponsor.",
  },
  {
    q: "What should guests know about getting there?",
    a: "Backland is intentionally secluded. Guests receive detailed arrival instructions before their stay, and we recommend following Backland’s directions rather than relying only on map apps.",
  },
];

const QUICK_FACTS = [
  "10 luxury suites",
  "Ideal for intimate overnight groups",
  "Chef-prepared dining in onsite restaurant",
  "Private natural setting on 160 acres",
  "Stargazing, kayaking, trails, beach, and campfire",
  "Near the Grand Canyon, Flagstaff, Sedona, and Williams",
  "Assistance coordinating offsite adventures and local experiences",
];

const ONSITE_ACTIVITY_OPTIONS = [
  "Stargazing",
  "Campfire / s’mores",
  "Kayaking",
  "Beach / pond time",
  "Guided nature walk",
  "Disc golf / lawn games",
  "Team-building",
  "Wellness / yoga",
  "Other",
];

const OFFSITE_ACTIVITY_OPTIONS = [
  "Grand Canyon tour",
  "Sedona tour",
  "Helicopter tour",
  "Jeep tour",
  "Scenic day trip",
  "Photography experience",
  "Custom local adventure",
  "Not sure yet",
];


type GroupsCtxValue = {
  openForm: () => void;
  openSample: (anchor: string) => void;
  content: GroupsEventsContent | null;
  cards: GeCard[];
  faqs: GroupsEventsFaq[];
  gallery: GroupsEventsGalleryImage[];
};
const GroupsCtx = createContext<GroupsCtxValue | null>(null);
function useGroupsCtx(): GroupsCtxValue {
  const c = useContext(GroupsCtx);
  if (!c) throw new Error("GroupsCtx not provided");
  return c;
}

export default function GroupsEventsCategoryPage(_props: CategoryComponentProps) {
  const [formOpen, setFormOpen] = useState(false);
  const [sampleAnchor, setSampleAnchor] = useState<string | null>(null);
  const openForm = useCallback(() => setFormOpen(true), []);
  const openSample = useCallback((a: string) => setSampleAnchor(a), []);

  const fetchData = useServerFn(getGroupsEventsPageData);
  const { data } = useQuery({
    queryKey: ["groups-events", "page"],
    queryFn: () => fetchData(),
  });

  const ctx = useMemo(
    () => ({
      openForm,
      openSample,
      content: data?.content ?? null,
      cards: data?.cards ?? [],
      faqs: data?.faqs ?? [],
      gallery: data?.gallery ?? [],
    }),
    [openForm, openSample, data],
  );

  // Honor legacy hash anchors that pointed at sample sections
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash.replace("#", "");
    if (hash && SAMPLES.some((s) => s.anchor === hash)) {
      setSampleAnchor(hash);
    }
  }, []);

  return (
    <GroupsCtx.Provider value={ctx}>
      <div className="bg-background text-foreground">
        <Hero />
        <QuickFactsRow />
        <EventTypeCards />
        <FaqSection />
        <div className="relative">
          <InquiryCtaSection />
          <StickyInquiryButton />
          <FinalCta />
        </div>
      </div>
      <SampleDialog
        anchor={sampleAnchor}
        onClose={() => setSampleAnchor(null)}
      />
      <InquiryDialog open={formOpen} onClose={() => setFormOpen(false)} />
    </GroupsCtx.Provider>
  );
}

/* ---------------- Hero ---------------- */

function Hero() {
  const { openForm, content } = useGroupsCtx();
  const heroImg = content?.hero_image_url || pathHero.url;
  const kicker = content?.hero_kicker || "Private Groups & Events";
  const title = content?.hero_title || "Private Groups & Events at Backland";
  const subtitle =
    content?.hero_subtitle ||
    "A secluded nature resort for intimate events, retreats, reunions, and unforgettable overnight gatherings.";
  const ctaLabel = content?.hero_cta_label || "Inquire About an Event";
  return (
    <>
      <section className="mobile-hero relative w-full overflow-hidden vignette aspect-[4/5] sm:aspect-[16/10] max-h-[78svh]">
        <img
          src={heroImg}
          alt="Backland private groups and events hero"
          className="absolute inset-0 w-full h-full object-cover ken-burns"
          loading="eager"
          decoding="async"
          fetchPriority="high"
          draggable={false}
        />
        <div className="mobile-hero-scrim" />
        <div className="mobile-hero-copy px-7 flex flex-col items-start">
          <div className="mobile-hero-kicker text-[10px] tracking-[0.4em] uppercase mb-3 text-shadow-soft">
            {kicker}
          </div>
          <h1 className="mobile-hero-title font-serif text-4xl sm:text-5xl leading-[1.05] text-shadow-soft max-w-[16ch] whitespace-pre-line">
            {title}
          </h1>
          <p className="mobile-hero-subtitle mt-3 text-sm sm:text-base font-light max-w-[42ch] text-shadow-soft">
            {subtitle}
          </p>
        </div>
      </section>
      <div className="bg-background -mt-px relative z-10 flex justify-center py-6">
        <button
          type="button"
          onClick={openForm}
          className="inline-flex items-center justify-center rounded-full border border-gold/80 bg-black/30 px-6 py-3 text-[11px] tracking-[0.3em] uppercase text-gold backdrop-blur-md hover:bg-gold hover:text-black transition cursor-pointer"
        >
          {ctaLabel}
        </button>
      </div>
    </>
  );
}

function QuickFactsRow() {
  return (
    <section className="px-6 sm:px-8 py-12 sm:py-16 max-w-4xl mx-auto">
      <p className="text-base sm:text-lg text-foreground/80 font-light leading-relaxed text-center max-w-3xl mx-auto">
        Host your group on 160 private acres surrounded by national forest - space to truly connect. Backland is best suited for intimate overnight events where the setting is part of the experience.
      </p>
      <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
        {QUICK_FACTS.map((f) => (
          <li
            key={f}
            className="flex items-start gap-3 text-sm text-foreground/80"
          >
            <span className="mt-2 inline-block h-1 w-1 rounded-full bg-gold/80 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ---------------- Event type cards ---------------- */

function EventTypeCards() {
  const { openSample, cards: cmsCards } = useGroupsCtx();
  const fallbackImages: Record<string, string> = {
    weddings: patio.url,
    "corporate-retreats": lounge.url,
    "family-reunions": suiteFamily.url,
    "wellness-retreats": tentNight.url,
    "private-celebrations": hotTub.url,
  };
  const cards =
    cmsCards.length > 0
      ? cmsCards.map((c) => ({
          id: c.id,
          anchor: c.slug,
          title: c.title,
          description: c.description ?? "",
          bestFor: c.best_for ?? "",
          cta: c.cta_label ?? "View Sample",
          image: c.image_url || fallbackImages[c.slug] || pathHero.url,
        }))
      : EVENT_CARDS;
  return (
    <section className="px-6 sm:px-8 py-14 sm:py-20 bg-background">
      <div className="max-w-3xl mx-auto text-center mb-12">
        <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-4">
          Events
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl text-foreground leading-tight">
          What Kind of Event Are You Planning?
        </h2>
        <p className="mt-5 text-sm sm:text-base text-foreground/75 font-light leading-relaxed">
          Whether you are planning a wedding weekend, company retreat, family reunion, wellness retreat, or private celebration, Backland offers a rare setting where your group can slow down, reconnect, and experience nature without giving up comfort.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {cards.map((card) => (
          <article
            key={card.id}
            className="group flex flex-col overflow-hidden rounded-2xl border border-foreground/10 bg-card/60 backdrop-blur"
          >
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src={card.image}
                alt={card.title}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            </div>
            <div className="flex flex-col flex-1 p-6">
              <h3 className="font-serif text-xl sm:text-2xl text-foreground leading-snug">
                {card.title}
              </h3>
              <p className="mt-3 text-sm text-foreground/75 leading-relaxed">
                {card.description}
              </p>
              <p className="mt-4 text-xs uppercase tracking-[0.18em] text-gold/80">
                Best for
              </p>
              <p className="mt-1 text-sm text-foreground/70 leading-relaxed">
                {card.bestFor}
              </p>
              <div className="mt-auto pt-6">
                <button
                  type="button"
                  onClick={() => openSample(card.anchor)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase text-gold border border-gold/40 rounded-lg hover:bg-gold/10 hover:border-gold/60 transition cursor-pointer"
                >
                  {card.cta}
                  <span aria-hidden="true">→</span>
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Fit section ---------------- */

function FitSection() {
  return (
    <section className="px-6 sm:px-8 py-14 sm:py-20 border-t border-foreground/10">
      <div className="max-w-3xl mx-auto text-center mb-12">
        <h2 className="font-serif text-3xl sm:text-4xl text-foreground">
          Is Backland Right for Your Event?
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
        <FitColumn
          title="Backland is a great fit if you want:"
          tone="good"
          items={[
            "Private use of 160 acres of pure natural seclusion",
            "A smaller, more intimate overnight event suitable for 10 lodging rooms",
            "Lodging, meals, and experiences in one place",
            "A setting that feels elevated but not formal or corporate",
            "A guest experience built around connection, stillness, dark skies, and nature",
            "Help planning a memorable itinerary with both onsite and offsite experiences",
          ]}
        />
        <FitColumn
          title="Backland may not be the right fit if you need:"
          tone="bad"
          items={[
            "A large ballroom-style venue",
            "Hundreds of attendees",
            "Loud late-night amplified music",
            "A traditional hotel or conference center layout",
          ]}
        />
      </div>
    </section>
  );
}

function FitColumn({
  title,
  items,
  tone,
}: {
  title: string;
  items: string[];
  tone: "good" | "bad";
}) {
  return (
    <div className="rounded-2xl border border-foreground/10 bg-card/40 p-7">
      <h3 className="font-serif text-xl text-foreground mb-5 leading-snug">
        {title}
      </h3>
      <ul className="space-y-3">
        {items.map((i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-foreground/80">
            <span
              className={`mt-[6px] inline-block h-1.5 w-1.5 rounded-full shrink-0 ${
                tone === "good" ? "bg-gold" : "bg-foreground/30"
              }`}
            />
            <span>{i}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------------- Activities ---------------- */

function ActivitiesSection() {
  const [openAmenity, setOpenAmenity] = useState<OnsiteAmenity | null>(null);
  useGalleryGestureLock(openAmenity !== null);

  const fetchOnsite = useServerFn(getOnsitePageData);
  const { data: onsiteData } = useQuery({
    queryKey: ["onsite", "page", null],
    queryFn: () => fetchOnsite(),
  });
  const onsiteAmenities = onsiteData?.amenities ?? [];

  return (
    <section
      id="activities"
      className="px-6 sm:px-8 py-14 sm:py-20 bg-background border-t border-foreground/10"
    >
      <div className="max-w-3xl mx-auto text-center mb-10">
        <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-4">
          Activities
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl text-foreground leading-tight">
          Activities &amp; Experiences We Can Help Coordinate
        </h2>
        <p className="mt-5 text-sm sm:text-base text-foreground/75 leading-relaxed">
          Backland is a destination on its own, but it can also serve as a private basecamp for some of Northern Arizona’s most memorable experiences. We can help your group think through the right mix of onsite activities, guided adventures, scenic tours, and downtime.
        </p>
      </div>

      <div className="max-w-6xl mx-auto">
        <Accordion type="multiple" className="space-y-4">
          <AccordionItem
            value="onsite"
            className="rounded-2xl border border-foreground/10 bg-card/40 px-5"
          >
            <AccordionTrigger className="font-serif text-lg sm:text-xl text-foreground hover:no-underline">
              See Onsite Experiences at Backland
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 pt-2 pb-4">
                {onsiteAmenities.map((am) => (
                  <button
                    key={am.id}
                    type="button"
                    onClick={() => setOpenAmenity(am)}
                    className="group relative block w-full overflow-hidden rounded-xl border border-foreground/10 bg-muted aspect-[4/3] focus:outline-none focus:ring-2 focus:ring-gold/60 active:scale-[0.99] transition-transform"
                  >
                    {am.cover_image_url ? (
                      <img
                        src={am.cover_image_url}
                        alt={am.label}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                        draggable={false}
                      />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-foreground/10 to-foreground/5" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 text-left">
                      <h4 className="font-serif text-2xl sm:text-3xl text-foreground text-shadow-soft">
                        {am.label}
                      </h4>
                    </div>
                  </button>
                ))}
                {onsiteAmenities.length === 0 && (
                  <ul className="col-span-full grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                    {ONSITE_EXPERIENCES.map((e) => (
                      <li
                        key={e}
                        className="flex items-start gap-3 text-sm text-foreground/80"
                      >
                        <span className="mt-2 h-1 w-1 rounded-full bg-gold/80 shrink-0" />
                        <span>{e}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="offsite"
            className="rounded-2xl border border-foreground/10 bg-card/40 px-5"
          >
            <AccordionTrigger className="font-serif text-lg sm:text-xl text-foreground hover:no-underline">
              See Offsite Adventures We Can Help Arrange
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 pt-2 pb-4">
                {OFFSITE_EXPERIENCES.map((e) => (
                  <div
                    key={e.title}
                    className="rounded-xl border border-foreground/10 bg-card/40 p-5 sm:p-6"
                  >
                    <h4 className="font-serif text-xl sm:text-2xl text-foreground leading-tight">
                      {e.title}
                    </h4>
                    <p className="mt-2 text-xs sm:text-sm text-foreground/75 leading-relaxed">
                      {e.body}
                    </p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-foreground/55 italic leading-relaxed pb-4">
                Some offsite activities are provided by trusted third-party guides, outfitters, and tour operators. Availability, pricing, transportation, group size limits, and safety requirements vary by provider. We can help coordinate options, but final details may depend on the activity and vendor.
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      <Dialog
        open={openAmenity !== null}
        onOpenChange={(o) => !o && setOpenAmenity(null)}
      >
        <DialogContent className="gallery-zoom-lock max-w-lg lg:max-w-[1000px] bg-background border-foreground/15 max-h-[85svh] overflow-y-auto">
          {openAmenity && (
            <>
              <DialogHeader>
                <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-1">
                  Amenity
                </div>
                <DialogTitle className="font-serif text-3xl text-foreground">
                  {openAmenity.label}
                </DialogTitle>
                {openAmenity.description && (
                  <DialogDescription className="text-foreground/70 font-light whitespace-pre-line text-left">
                    {openAmenity.description}
                  </DialogDescription>
                )}
              </DialogHeader>
              {openAmenity.gallery.length > 0 && (
                <div className="gallery-zoom-lock mt-4 space-y-4">
                  {openAmenity.gallery.map((img, i) => (
                    <img
                      key={img.id}
                      src={img.url}
                      alt={`${openAmenity.label} ${i + 1}`}
                      className="w-full rounded-md border border-foreground/10"
                      loading="lazy"
                      draggable={false}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

/* ---------------- What we provide ---------------- */

function ProvidesSection() {
  return (
    <section
      id="what-we-provide"
      className="px-6 sm:px-8 py-14 sm:py-20 border-t border-foreground/10"
    >
      <div className="max-w-3xl mx-auto text-center mb-12">
        <h2 className="font-serif text-3xl sm:text-4xl text-foreground leading-tight">
          What Backland Provides — and What You’ll Need to Coordinate
        </h2>
        <p className="mt-5 text-sm sm:text-base text-foreground/75 leading-relaxed">
          Backland is a lodging, dining, and private nature venue. We can help create an unforgettable group experience, but we are not a full-service event production company. For larger or more complex events, the event sponsor may need to coordinate outside vendors, rentals, staffing, and logistics.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
        <FitColumn
          title="Backland Typically Provides"
          tone="good"
          items={[
            "Overnight lodging in Backland’s luxury nature suites",
            "Our 160-acre private natural venue",
            "1,800 sq ft glass-walled lobby, restaurant, and gathering space",
            "Onsite food service — group meals, breakfast, dinner, snacks, and beverage service",
            "Option to use Backland food service or coordinate approved outside catering",
            "Help arranging offsite activities and local experiences",
            "Access to onsite activities such as unspoiled dark skies, kayaking, beach time, trails, campfire, and lawn games",
            "General planning guidance related to lodging, meals, timing, guest flow, and onsite experience",
            "Parking access for overnight guests",
            "Basic coordination related to Backland services and property use",
          ]}
        />
        <FitColumn
          title="Event Sponsor Typically Coordinates"
          tone="bad"
          items={[
            "Dedicated event coordinator or planner",
            "Event design, run-of-show management, and day-of coordination",
            "Equipment rentals",
            "Tent rentals",
            "Tables, chairs, linens, décor, floral, lighting, staging, or specialty rentals",
            "Vendor contracts and vendor management",
            "Setup and teardown",
            "Additional staffing beyond Backland’s normal service team",
            "Transportation or shuttles, if needed",
            "Mobile restrooms if more than 30 people will be onsite",
            "Permits, insurance, or special requirements related to the specific event, if applicable",
            "Guest communication beyond Backland’s normal stay details",
          ]}
        />
      </div>

      <div className="mt-10 max-w-3xl mx-auto rounded-xl border border-gold/30 bg-gold/5 px-6 py-5">
        <p className="text-sm text-foreground/85 leading-relaxed">
          For simple overnight retreats, family gatherings, and small private events, the coordination needs may be minimal. For weddings, larger gatherings, tented events, or events with more than 30 people onsite, we strongly recommend having a dedicated event coordinator.
        </p>
      </div>
    </section>
  );
}

/* ---------------- Sample dialog ---------------- */

function SampleDialog({
  anchor,
  onClose,
}: {
  anchor: string | null;
  onClose: () => void;
}) {
  const sample = anchor ? SAMPLES.find((s) => s.anchor === anchor) ?? null : null;
  return (
    <Dialog open={sample !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl lg:max-w-3xl bg-background border-foreground/15 max-h-[90svh] overflow-y-auto">
        {sample && (
          <>
            <DialogHeader>
              <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-1">
                Sample Experience
              </div>
              <DialogTitle className="font-serif text-3xl text-foreground text-left">
                {sample.title}
              </DialogTitle>
              <DialogDescription className="text-[11px] uppercase tracking-[0.2em] text-gold/80 text-left">
                {sample.length} · {sample.bestFor}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 pt-2">
              <p className="text-sm text-foreground/75 italic">{sample.price}</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <SampleList title="Sample inclusions" items={sample.inclusions} tone="good" />
                <SampleList
                  title="Event sponsor may need to provide"
                  items={sample.sponsor}
                  tone="bad"
                />
              </div>

              <div>
                <h4 className="text-xs uppercase tracking-[0.2em] text-gold/80 mb-3">
                  Sample itinerary
                </h4>
                <div className="space-y-5">
                  {sample.days.map((d) => (
                    <div key={d.title}>
                      <p className="font-serif text-base text-foreground mb-2">
                        {d.title}
                      </p>
                      <ul className="space-y-1.5">
                        {d.items.map((it) => (
                          <li
                            key={it}
                            className="flex items-start gap-3 text-sm text-foreground/75"
                          >
                            <span className="mt-2 h-1 w-1 rounded-full bg-gold/70 shrink-0" />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              <SampleInquireButton onClose={onClose} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SampleInquireButton({ onClose }: { onClose: () => void }) {
  const { openForm } = useGroupsCtx();
  return (
    <div className="pt-2">
      <Button
        type="button"
        onClick={() => {
          onClose();
          openForm();
        }}
        className="rounded-full px-7 py-5 text-[11px] tracking-[0.3em] uppercase bg-gold text-black hover:bg-gold/90"
      >
        Inquire about this experience
      </Button>
    </div>
  );
}

function SampleList({
  title,
  items,
  tone,
}: {
  title: string;
  items: string[];
  tone: "good" | "bad";
}) {
  return (
    <div>
      <h4 className="text-xs uppercase tracking-[0.2em] text-gold/80 mb-3">
        {title}
      </h4>
      <ul className="space-y-2">
        {items.map((i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-foreground/75">
            <span
              className={`mt-[6px] inline-block h-1.5 w-1.5 rounded-full shrink-0 ${
                tone === "good" ? "bg-gold" : "bg-foreground/30"
              }`}
            />
            <span>{i}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------------- FAQ ---------------- */

function getScrollParent(el: HTMLElement): HTMLElement | Window {
  let parent = el.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY;
    if ((overflowY === "auto" || overflowY === "scroll") && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return window;
}

function FaqSection() {
  const [showMore, setShowMore] = useState(false);
  const [openAmenity, setOpenAmenity] = useState<OnsiteAmenity | null>(null);
  useGalleryGestureLock(openAmenity !== null);

  const { faqs: cmsFaqs } = useGroupsCtx();
  const fetchOnsite = useServerFn(getOnsitePageData);
  const { data: onsiteData } = useQuery({
    queryKey: ["onsite", "page", null],
    queryFn: () => fetchOnsite(),
  });
  const onsiteAmenities = onsiteData?.amenities ?? [];

  const faqList =
    cmsFaqs.length > 0
      ? cmsFaqs.map((f) => ({ q: f.question, a: f.answer }))
      : FAQS;
  const firstFaq = faqList[0];
  const restFaqs = faqList.slice(1);

  const faqItemClass =
    "rounded-xl border border-foreground/10 bg-card/40 px-5";

  const scrollQuestionIntoView = (el: HTMLButtonElement) => {
    const targetOffset = 92;

    const alignTrigger = () => {
      if (!el?.isConnected) return;
      const scroller = getScrollParent(el);
      const triggerRect = el.getBoundingClientRect();
      if (scroller instanceof Window) {
        window.scrollTo({ top: triggerRect.top + window.scrollY - targetOffset, behavior: "auto" });
        return;
      }

      const scrollerRect = scroller.getBoundingClientRect();
      scroller.scrollTo({
        top: scroller.scrollTop + triggerRect.top - scrollerRect.top - targetOffset,
        behavior: "auto",
      });
    };

    [1, 80, 180, 360, 700].forEach((delay) => {
      window.setTimeout(() => requestAnimationFrame(alignTrigger), delay);
    });
  };

  return (
    <section className="px-6 sm:px-8 py-14 sm:py-20 border-t border-foreground/10">
      <div className="max-w-3xl mx-auto">
        <h2 className="font-serif text-3xl sm:text-4xl text-foreground text-center mb-10">
          Frequently Asked Questions
        </h2>

        <Accordion type="single" collapsible className="space-y-3">
          {/* 1. How many people can Backland accommodate? */}
          <AccordionItem value="faq-0" className={faqItemClass}>
            <AccordionTrigger onClick={(e) => scrollQuestionIntoView(e.currentTarget)} className="text-left text-sm sm:text-base text-foreground hover:no-underline">
              {firstFaq.q}
            </AccordionTrigger>
            <AccordionContent>
              <p className="text-sm text-foreground/75 leading-relaxed pb-3">
                {firstFaq.a}
              </p>
            </AccordionContent>
          </AccordionItem>

          {/* 2. Is Backland right for your event? */}
          <AccordionItem value="fit" className={faqItemClass}>
            <AccordionTrigger onClick={(e) => scrollQuestionIntoView(e.currentTarget)} className="text-left text-sm sm:text-base text-foreground hover:no-underline">
              Is Backland right for your event?
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-4 pt-2">
                <FitColumn
                  title="Backland is a great fit if you want:"
                  tone="good"
                  items={[
                    "Private use of 160 acres of pure natural seclusion",
                    "A smaller, more intimate overnight event suitable for 10 lodging rooms",
                    "Lodging, meals, and experiences in one place",
                    "A setting that feels elevated but not formal or corporate",
                    "A guest experience built around connection, stillness, dark skies, and nature",
                    "Help planning a memorable itinerary with both onsite and offsite experiences",
                  ]}
                />
                <FitColumn
                  title="Backland may not be the right fit if you need:"
                  tone="bad"
                  items={[
                    "A large ballroom-style venue",
                    "Hundreds of attendees",
                    "Loud late-night amplified music",
                    "A traditional hotel or conference center layout",
                  ]}
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 3. What activities and experiences can Backland help coordinate? */}
          <AccordionItem value="activities" className={faqItemClass}>
            <AccordionTrigger onClick={(e) => scrollQuestionIntoView(e.currentTarget)} className="text-left text-sm sm:text-base text-foreground hover:no-underline">
              What activities and experiences can Backland help coordinate?
            </AccordionTrigger>
            <AccordionContent>
              <div className="pt-2 pb-4 space-y-6">
                <p className="text-sm text-foreground/75 leading-relaxed">
                  Backland is a destination on its own, but it can also serve as a private basecamp for some of Northern Arizona’s most memorable experiences. We can help your group think through the right mix of onsite activities, guided adventures, scenic tours, and downtime.
                </p>

                <div>
                  <h4 className="text-xs uppercase tracking-[0.2em] text-gold/80 mb-3">
                    Onsite experiences
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {onsiteAmenities.map((am) => (
                      <button
                        key={am.id}
                        type="button"
                        onClick={() => setOpenAmenity(am)}
                        className="group relative block w-full overflow-hidden rounded-xl border border-foreground/10 bg-muted aspect-[4/3] focus:outline-none focus:ring-2 focus:ring-gold/60 active:scale-[0.99] transition-transform"
                      >
                        {am.cover_image_url ? (
                          <img
                            src={am.cover_image_url}
                            alt={am.label}
                            loading="lazy"
                            decoding="async"
                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                            draggable={false}
                          />
                        ) : (
                          <div className="absolute inset-0 bg-gradient-to-br from-foreground/10 to-foreground/5" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 p-4 text-left">
                          <h5 className="font-serif text-lg text-foreground text-shadow-soft">
                            {am.label}
                          </h5>
                        </div>
                      </button>
                    ))}
                    {onsiteAmenities.length === 0 &&
                      ONSITE_EXPERIENCES.map((e) => (
                        <div
                          key={e}
                          className="flex items-start gap-3 text-sm text-foreground/80"
                        >
                          <span className="mt-2 h-1 w-1 rounded-full bg-gold/80 shrink-0" />
                          <span>{e}</span>
                        </div>
                      ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-xs uppercase tracking-[0.2em] text-gold/80 mb-3">
                    Offsite adventures we can help arrange
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {OFFSITE_EXPERIENCES.map((e) => (
                      <div
                        key={e.title}
                        className="rounded-xl border border-foreground/10 bg-card/40 p-4"
                      >
                        <h5 className="font-serif text-base sm:text-lg text-foreground leading-tight">
                          {e.title}
                        </h5>
                        <p className="mt-1.5 text-xs text-foreground/75 leading-relaxed">
                          {e.body}
                        </p>
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-foreground/55 italic leading-relaxed">
                    Some offsite activities are provided by trusted third-party guides, outfitters, and tour operators. Availability, pricing, transportation, group size limits, and safety requirements vary by provider.
                  </p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 4. What does Backland provide — and what will I need to coordinate? */}
          <AccordionItem value="provides" className={faqItemClass}>
            <AccordionTrigger onClick={(e) => scrollQuestionIntoView(e.currentTarget)} className="text-left text-sm sm:text-base text-foreground hover:no-underline">
              What does Backland provide — and what will I need to coordinate?
            </AccordionTrigger>
            <AccordionContent>
              <div className="pt-2 pb-4 space-y-6">
                <p className="text-sm text-foreground/75 leading-relaxed">
                  Backland is a lodging, dining, and private nature venue. We can help create an unforgettable group experience, but we are not a full-service event production company. For larger or more complex events, the event sponsor may need to coordinate outside vendors, rentals, staffing, and logistics.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FitColumn
                    title="Backland Typically Provides"
                    tone="good"
                    items={[
                      "Overnight lodging in Backland’s luxury nature suites",
                      "Our 160-acre private natural venue",
                      "1,800 sq ft glass-walled lobby, restaurant, and gathering space",
                      "Onsite food service — group meals, breakfast, dinner, snacks, and beverage service",
                      "Option to use Backland food service or coordinate approved outside catering",
                      "Help arranging offsite activities and local experiences",
                      "Access to onsite activities such as unspoiled dark skies, kayaking, beach time, trails, campfire, and lawn games",
                      "General planning guidance related to lodging, meals, timing, guest flow, and onsite experience",
                      "Parking access for overnight guests",
                      "Basic coordination related to Backland services and property use",
                    ]}
                  />
                  <FitColumn
                    title="Event Sponsor Typically Coordinates"
                    tone="bad"
                    items={[
                      "Dedicated event coordinator or planner",
                      "Event design, run-of-show management, and day-of coordination",
                      "Equipment rentals",
                      "Tent rentals",
                      "Tables, chairs, linens, décor, floral, lighting, staging, or specialty rentals",
                      "Vendor contracts and vendor management",
                      "Setup and teardown",
                      "Additional staffing beyond Backland’s normal service team",
                      "Transportation or shuttles, if needed",
                      "Mobile restrooms if more than 30 people will be onsite",
                      "Permits, insurance, or special requirements related to the specific event, if applicable",
                      "Guest communication beyond Backland’s normal stay details",
                    ]}
                  />
                </div>
                <div className="rounded-xl border border-gold/30 bg-gold/5 px-5 py-4">
                  <p className="text-sm text-foreground/85 leading-relaxed">
                    For simple overnight retreats, family gatherings, and small private events, the coordination needs may be minimal. For weddings, larger gatherings, tented events, or events with more than 30 people onsite, we strongly recommend having a dedicated event coordinator.
                  </p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <Collapsible open={showMore} onOpenChange={setShowMore} className="mt-4">
          <CollapsibleTrigger className="group w-full flex items-center justify-center gap-2 rounded-full border border-foreground/15 bg-card/40 px-5 py-3 text-[11px] tracking-[0.3em] uppercase text-foreground/80 hover:text-foreground hover:border-foreground/30 transition cursor-pointer">
            <span>{showMore ? "Hide FAQ" : "See more FAQ"}</span>
            <ChevronDown
              className={`h-4 w-4 transition-transform ${showMore ? "rotate-180" : ""}`}
            />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <Accordion type="single" collapsible className="space-y-3 mt-4">
              {restFaqs.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`faq-rest-${i}`}
                  className={faqItemClass}
                >
                  <AccordionTrigger onClick={(e) => scrollQuestionIntoView(e.currentTarget)} className="text-left text-sm sm:text-base text-foreground hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-sm text-foreground/75 leading-relaxed pb-3">
                      {f.a}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CollapsibleContent>
        </Collapsible>
      </div>

      <Dialog
        open={openAmenity !== null}
        onOpenChange={(o) => !o && setOpenAmenity(null)}
      >
        <DialogContent className="gallery-zoom-lock max-w-lg lg:max-w-[1000px] bg-background border-foreground/15 max-h-[85svh] overflow-y-auto">
          {openAmenity && (
            <>
              <DialogHeader>
                <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-1">
                  Amenity
                </div>
                <DialogTitle className="font-serif text-3xl text-foreground">
                  {openAmenity.label}
                </DialogTitle>
                {openAmenity.description && (
                  <DialogDescription className="text-foreground/70 font-light whitespace-pre-line text-left">
                    {openAmenity.description}
                  </DialogDescription>
                )}
              </DialogHeader>
              {openAmenity.gallery.length > 0 && (
                <div className="gallery-zoom-lock mt-4 space-y-4">
                  {openAmenity.gallery.map((img, i) => (
                    <img
                      key={img.id}
                      src={img.url}
                      alt={`${openAmenity.label} ${i + 1}`}
                      className="w-full rounded-md border border-foreground/10"
                      loading="lazy"
                      draggable={false}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

/* ---------------- Inquiry form ---------------- */

type FormState = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  event_type: string;
  preferred_dates: string;
  dates_flexible: string;
  estimated_overnight_guests: string;
  estimated_total_onsite: string;
  nights_desired: string;
  full_property_buyout: string;
  food_plan: string;
  onsite_activities: string[];
  offsite_activities: string[];
  has_coordinator: string;
  needs_rentals: string;
  needs_transport: string;
  budget_range: string;
  priorities: string;
  notes: string;
};

const initialForm: FormState = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  event_type: "",
  preferred_dates: "",
  dates_flexible: "",
  estimated_overnight_guests: "",
  estimated_total_onsite: "",
  nights_desired: "",
  full_property_buyout: "",
  food_plan: "",
  onsite_activities: [],
  offsite_activities: [],
  has_coordinator: "",
  needs_rentals: "",
  needs_transport: "",
  budget_range: "",
  priorities: "",
  notes: "",
};

function InquiryCtaSection() {
  const { openForm } = useGroupsCtx();
  return (
    <section
      id="inquiry"
      className="px-6 sm:px-8 py-16 sm:py-24 bg-background border-t border-foreground/10 scroll-mt-20"
    >
      <div className="max-w-3xl mx-auto text-center">
        <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-4">
          Event Inquiry
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl text-foreground leading-tight">
          Start Planning Your Backland Event
        </h2>
        <p className="mt-5 text-sm sm:text-base text-foreground/75 leading-relaxed">
          Tell us a little about your group, your ideal dates, and what kind of experience you are hoping to create. Our team will review your inquiry and follow up with availability, options, and next steps.
        </p>
      </div>
    </section>
  );
}

function StickyInquiryButton() {
  const { openForm } = useGroupsCtx();
  return (
    <div className="sticky top-[calc(env(safe-area-inset-top)+5rem)] z-40 flex justify-center pointer-events-none -mt-6 mb-10 px-4">
      <Button
        type="button"
        onClick={openForm}
        className="pointer-events-auto rounded-full px-6 py-5 text-[10px] sm:text-[11px] tracking-[0.3em] uppercase bg-gold text-black hover:bg-gold/90 shadow-xl shadow-black/50"
      >
        Start an Event Inquiry
      </Button>
    </div>
  );
}

function InquiryDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl lg:max-w-3xl bg-background border-foreground/15 max-h-[90svh] overflow-hidden flex flex-col p-0" hideCloseButton>
        <DialogClose className="absolute right-4 top-4 z-[60] h-10 w-10 inline-flex items-center justify-center rounded-full bg-background/95 backdrop-blur-sm border border-foreground/15 shadow-lg opacity-80 hover:opacity-100 transition-opacity cursor-pointer">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogClose>
        <div className="overflow-y-auto flex-1 p-6 pt-14">
          <DialogHeader>
            <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-1">
              Event Inquiry
            </div>
            <DialogTitle className="font-serif text-2xl sm:text-3xl text-foreground text-left">
              Start Planning Your Backland Event
            </DialogTitle>
            <DialogDescription className="text-foreground/70 font-light text-left">
              This is an inquiry for a private group or event — not a standard room booking. Our events team will follow up with availability, options, and next steps.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-4">
            <InquiryForm />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InquiryForm() {
  const submitFn = useServerFn(submitEventInquiry);
  const [form, setForm] = useState<FormState>(initialForm);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const successRef = useRef<HTMLDivElement | null>(null);

  const mutation = useMutation({
    mutationFn: (data: FormState) => submitFn({ data }),
    onSuccess: () => {
      setErrorMsg(null);
      if (typeof window !== "undefined") {
        const w = window as unknown as { dataLayer?: Record<string, unknown>[] };
        w.dataLayer = w.dataLayer || [];
        w.dataLayer.push({
          event: "groups_inquiry_submit",
          form_name: "groups_event_inquiry",
          form_location: "groups_events_page",
          event_type: form.event_type || null,
          estimated_overnight_guests: form.estimated_overnight_guests || null,
          estimated_total_onsite: form.estimated_total_onsite || null,
          budget_range: form.budget_range || null,
        });
      }
      setTimeout(() => successRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
    },
    onError: (e: unknown) => {
      setErrorMsg(e instanceof Error ? e.message : "Something went wrong. Please try again.");
    },
  });

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const toggleMulti = (k: "onsite_activities" | "offsite_activities", value: string) =>
    setForm((f) => ({
      ...f,
      [k]: f[k].includes(value) ? f[k].filter((x) => x !== value) : [...f[k], value],
    }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    if (!form.first_name || !form.last_name || !form.email) {
      setErrorMsg("Please share your name and email so we can follow up.");
      return;
    }
    mutation.mutate(form);
  };

  if (mutation.isSuccess) {
    return (
      <div
        ref={successRef}
        className="rounded-2xl border border-gold/30 bg-gold/5 p-8 text-center"
      >
        <h3 className="font-serif text-2xl text-foreground mb-3">Thank you</h3>
        <p className="text-sm sm:text-base text-foreground/80 leading-relaxed max-w-xl mx-auto">
          Thanks — we received your event inquiry. Our team will review your dates, group size, event goals, activity interests, and logistics needs and follow up with availability and next steps.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-10">
      <FieldGroup title="Contact Info">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="First name" required>
            <Input
              value={form.first_name}
              onChange={(e) => update("first_name", e.target.value)}
              required
            />
          </Field>
          <Field label="Last name" required>
            <Input
              value={form.last_name}
              onChange={(e) => update("last_name", e.target.value)}
              required
            />
          </Field>
          <Field label="Email" required>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              required
            />
          </Field>
          <Field label="Phone">
            <Input
              type="tel"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
            />
          </Field>
        </div>
      </FieldGroup>

      <FieldGroup title="Event Details">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Event type">
            <Select
              value={form.event_type}
              onChange={(v) => update("event_type", v)}
              options={[
                "Wedding / elopement",
                "Corporate retreat",
                "Family reunion",
                "Wellness or creative retreat",
                "Private celebration",
                "Other",
              ]}
            />
          </Field>
          <Field label="Preferred date or date range">
            <Input
              value={form.preferred_dates}
              onChange={(e) => update("preferred_dates", e.target.value)}
              placeholder="e.g. Oct 2026 or 10/12–10/14"
            />
          </Field>
          <Field label="Are your dates flexible?">
            <Select
              value={form.dates_flexible}
              onChange={(v) => update("dates_flexible", v)}
              options={["Yes", "No", "Somewhat"]}
            />
          </Field>
          <Field label="Number of nights">
            <Select
              value={form.nights_desired}
              onChange={(v) => update("nights_desired", v)}
              options={["1", "2", "3", "Flexible"]}
            />
          </Field>
          <Field label="Estimated overnight guests">
            <Input
              value={form.estimated_overnight_guests}
              onChange={(e) => update("estimated_overnight_guests", e.target.value)}
              placeholder="e.g. 18"
            />
          </Field>
          <Field label="Estimated total people onsite">
            <Input
              value={form.estimated_total_onsite}
              onChange={(e) => update("estimated_total_onsite", e.target.value)}
              placeholder="e.g. 24"
            />
          </Field>
          <Field label="Interested in a full-property buyout?">
            <Select
              value={form.full_property_buyout}
              onChange={(v) => update("full_property_buyout", v)}
              options={["Yes", "No", "Maybe"]}
            />
          </Field>
        </div>
      </FieldGroup>

      <FieldGroup title="Food & Beverage">
        <Field label="Food plan you are interested in">
          <Select
            value={form.food_plan}
            onChange={(v) => update("food_plan", v)}
            options={[
              "Backland food service",
              "Outside catering",
              "Combination of both",
              "Not sure yet",
            ]}
          />
        </Field>
      </FieldGroup>

      <FieldGroup title="Activities & Experiences">
        <MultiSelectField
          label="Onsite activities you are interested in"
          options={ONSITE_ACTIVITY_OPTIONS}
          value={form.onsite_activities}
          onToggle={(v) => toggleMulti("onsite_activities", v)}
        />
        <MultiSelectField
          label="Offsite activities you are interested in"
          options={OFFSITE_ACTIVITY_OPTIONS}
          value={form.offsite_activities}
          onToggle={(v) => toggleMulti("offsite_activities", v)}
        />
      </FieldGroup>

      <FieldGroup title="Planning & Logistics">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Will you have a coordinator?">
            <Select
              value={form.has_coordinator}
              onChange={(v) => update("has_coordinator", v)}
              options={["Yes", "No", "Not sure yet"]}
            />
          </Field>
          <Field label="Will you need rentals?">
            <Select
              value={form.needs_rentals}
              onChange={(v) => update("needs_rentals", v)}
              options={["Yes", "No", "Not sure yet"]}
            />
          </Field>
          <Field label="Need transportation?">
            <Select
              value={form.needs_transport}
              onChange={(v) => update("needs_transport", v)}
              options={["Yes", "No", "Not sure yet"]}
            />
          </Field>
        </div>
      </FieldGroup>

      <FieldGroup title="Planning & Budget">
        <Field label="Approximate budget range">
          <Select
            value={form.budget_range}
            onChange={(v) => update("budget_range", v)}
            options={[
              "Under $10k",
              "$10k–$20k",
              "$20k–$35k",
              "$35k+",
              "Not sure yet",
            ]}
          />
        </Field>
        <Field label="What matters most for this event?">
          <Textarea
            value={form.priorities}
            onChange={(e) => update("priorities", e.target.value)}
            rows={3}
          />
        </Field>
        <Field label="Anything else we should know?">
          <Textarea
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            rows={3}
          />
        </Field>
      </FieldGroup>

      {errorMsg && <p className="text-sm text-destructive">{errorMsg}</p>}

      <div className="pt-2">
        <Button
          type="submit"
          disabled={mutation.isPending}
          className="w-full sm:w-auto rounded-full px-8 py-6 text-[11px] tracking-[0.3em] uppercase bg-gold text-black hover:bg-gold/90"
        >
          {mutation.isPending ? "Sending…" : "Submit Event Inquiry"}
        </Button>
      </div>
    </form>
  );
}

function FieldGroup({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <fieldset className="space-y-4">
      <legend className="text-xs uppercase tracking-[0.25em] text-gold/80 mb-2">
        {title}
      </legend>
      <div className="space-y-4">{children}</div>
    </fieldset>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  const id = useMemo(() => `f-${Math.random().toString(36).slice(2, 9)}`, []);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs text-foreground/70">
        {label}
        {required ? <span className="text-gold ml-1">*</span> : null}
      </Label>
      <div id={id}>{children}</div>
    </div>
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ colorScheme: "dark" }}
      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      <option value="">Select…</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

function MultiSelectField({
  label,
  options,
  value,
  onToggle,
}: {
  label: string;
  options: string[];
  value: string[];
  onToggle: (v: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label className="text-xs text-foreground/70">{label}</Label>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const active = value.includes(o);
          return (
            <button
              key={o}
              type="button"
              onClick={() => onToggle(o)}
              className={`px-3 py-1.5 rounded-full border text-xs transition cursor-pointer ${
                active
                  ? "bg-gold text-black border-gold"
                  : "bg-transparent text-foreground/70 border-foreground/20 hover:border-foreground/40"
              }`}
            >
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- Event Gallery ---------------- */

const GALLERY_IMAGES = [
  patio.url,
  lounge.url,
  suiteFamily.url,
  tentNight.url,
  hotTub.url,
  bedMorning.url,
  suiteExterior.url,
  pathHero.url,
];

function FinalCta() {
  const { gallery } = useGroupsCtx();
  const images =
    gallery.length > 0
      ? gallery.map((g) => ({ src: g.image_url, alt: g.alt ?? "" }))
      : GALLERY_IMAGES.map((src) => ({ src, alt: "" }));
  return (
    <section className="gallery-zoom-lock px-3 sm:px-4 pb-20 pt-16 border-t border-foreground/10">
      <div className="text-center mb-8">
        <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-3">
          Event Gallery
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl text-foreground">
          Moments at Backland
        </h2>
      </div>
      <div className="gallery-zoom-lock max-w-5xl mx-auto columns-2 md:columns-3 gap-2 sm:gap-3 [&>figure]:mb-2 sm:[&>figure]:mb-3 [&>figure]:break-inside-avoid">
        {images.map(({ src, alt }, i) => {
          const aspect =
            i % 5 === 0 ? "aspect-[3/4]"
            : i % 5 === 1 ? "aspect-square"
            : i % 5 === 2 ? "aspect-[4/5]"
            : i % 5 === 3 ? "aspect-[4/3]"
            : "aspect-[3/4]";
          return (
            <figure
              key={`${src}-${i}`}
              className={`relative overflow-hidden bg-muted ${aspect}`}
            >
              <img
                src={src}
                alt={alt}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover hover:scale-[1.02] transition-transform duration-700"
                draggable={false}
              />
            </figure>
          );
        })}
      </div>
    </section>
  );
}
